import { useState, useCallback, useMemo } from 'react';

const TYPES = [
  'Kredyt gotówkowy',
  'Karta kredytowa',
  'Limit w koncie',
  'Raty za zakupy',
  'Kredyt samochodowy',
  'Pożyczka pozabankowa',
  'Inne zobowiązanie',
];

const MAX_DEBTS = 6;

interface Debt {
  type: string;
  saldo: number;
  rata: number;
}

function fmt(n: number): string {
  return Math.round(n).toLocaleString('pl-PL') + ' zł';
}

function annuity(K: number, r: number, n: number): number {
  const m = r / 1200;
  if (m === 0) return K / n;
  return (K * m) / (1 - Math.pow(1 + m, -n));
}

export default function ConsolidationCalculator() {
  const [debts, setDebts] = useState<Debt[]>([
    { type: 'Kredyt gotówkowy', saldo: 35000, rata: 950 },
    { type: 'Karta kredytowa', saldo: 12000, rata: 600 },
    { type: 'Raty za zakupy', saldo: 6000, rata: 450 },
  ]);
  const [cash, setCash] = useState(0);
  const [years, setYears] = useState(8);
  const [rate, setRate] = useState(9.5);

  const addDebt = useCallback(() => {
    if (debts.length >= MAX_DEBTS) return;
    setDebts((prev) => [...prev, { type: TYPES[0], saldo: 10000, rata: 400 }]);
  }, [debts.length]);

  const removeDebt = useCallback((i: number) => {
    setDebts((prev) => prev.filter((_, idx) => idx !== i));
  }, []);

  const updateDebt = useCallback((i: number, field: keyof Debt, value: string | number) => {
    setDebts((prev) =>
      prev.map((d, idx) => (idx === i ? { ...d, [field]: value } : d)),
    );
  }, []);

  const { saldo, oldRaty, loan, newRata, diff, totalNew, totalInt } = useMemo(() => {
    const saldoSum = debts.reduce((s, d) => s + d.saldo, 0);
    const oldRatySum = debts.reduce((s, d) => s + d.rata, 0);
    const loanSum = saldoSum + cash;
    const n = years * 12;
    const newRataVal = loanSum > 0 ? annuity(loanSum, rate, n) : 0;
    const diffVal = oldRatySum - newRataVal;
    const totalNewVal = newRataVal * n;
    const totalIntVal = totalNewVal - loanSum;
    return {
      saldo: saldoSum,
      oldRaty: oldRatySum,
      loan: loanSum,
      newRata: newRataVal,
      diff: diffVal,
      totalNew: totalNewVal,
      totalInt: totalIntVal,
    };
  }, [debts, cash, years, rate]);

  const isSaving = diff > 10;

  const yearsLabel = years === 1 ? 'rok' : years < 5 ? 'lata' : 'lat';

  return (
    <div>
      {/* Hero */}
      <section className="bg-foreground-950 text-white py-12 md:py-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary-400 mb-3">
            Kalkulator · Konsolidacja
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mx-auto max-w-2xl text-center leading-tight mb-4">
            Kilka rat zamień na jedną — policz, ile odzyskasz co miesiąc
          </h1>
          <p className="text-sm md:text-base text-white/60 mx-auto max-w-xl text-center leading-relaxed">
            Wpisz swoje kredyty, karty i limity. Kalkulator pokaże, jak zmieni się Twoja miesięczna rata po konsolidacji — i uczciwie doda, co w zamian: dłuższą spłatę. Decyzję ułatwi rozmowa z doradcą.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
            {/* Left — Inputs */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Twoje obecne zobowiązania
              </h3>

              {debts.map((debt, i) => (
                <div
                  key={i}
                  className="border border-background-200 rounded-xl p-3.5 md:p-4 mb-3"
                >
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <select
                      value={debt.type}
                      onChange={(e) => updateDebt(i, 'type', e.target.value)}
                      className="font-semibold text-sm text-foreground-950 bg-transparent border-none outline-none cursor-pointer max-w-[220px]"
                    >
                      {TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    {debts.length > 1 && (
                      <button
                        onClick={() => removeDebt(i)}
                        className="text-foreground-400 hover:text-red-600 hover:bg-red-50 rounded-md px-1.5 py-0.5 text-sm transition-colors cursor-pointer"
                        aria-label="Usuń zobowiązanie"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <label className="block text-[10px] font-bold text-foreground-500 uppercase tracking-wider mb-1">
                        Pozostało do spłaty
                      </label>
                      <div className="flex items-center border border-background-200 rounded-lg px-3 py-2 gap-2 bg-white focus-within:border-primary-400">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={debt.saldo.toLocaleString('pl-PL')}
                          onChange={(e) => {
                            const v = parseInt(e.target.value.replace(/[^\d]/g, ''), 10) || 0;
                            updateDebt(i, 'saldo', v);
                          }}
                          className="border-none outline-none w-full font-bold text-sm text-foreground-950 text-right bg-transparent"
                        />
                        <span className="text-xs text-foreground-400 font-semibold shrink-0">
                          zł
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-foreground-500 uppercase tracking-wider mb-1">
                        Rata / min. spłata mies.
                      </label>
                      <div className="flex items-center border border-background-200 rounded-lg px-3 py-2 gap-2 bg-white focus-within:border-primary-400">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={debt.rata.toLocaleString('pl-PL')}
                          onChange={(e) => {
                            const v = parseInt(e.target.value.replace(/[^\d]/g, ''), 10) || 0;
                            updateDebt(i, 'rata', v);
                          }}
                          className="border-none outline-none w-full font-bold text-sm text-foreground-950 text-right bg-transparent"
                        />
                        <span className="text-xs text-foreground-400 font-semibold shrink-0">
                          zł
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={addDebt}
                disabled={debts.length >= MAX_DEBTS}
                className="w-full border-2 border-dashed border-background-300 rounded-xl py-3 font-semibold text-sm text-primary-600 hover:border-primary-400 hover:bg-primary-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                + Dodaj zobowiązanie
              </button>

              {/* Cash slider */}
              <div className="mt-6">
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-xs font-bold text-foreground-500 uppercase tracking-wider">
                    Dodatkowa gotówka przy konsolidacji
                  </span>
                  <span className="text-sm font-bold text-foreground-950">
                    {cash.toLocaleString('pl-PL')} zł
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={150000}
                  step={5000}
                  value={cash}
                  onChange={(e) => setCash(Number(e.target.value))}
                  className="w-full accent-primary-500"
                />
                <div className="flex justify-between text-[10px] text-foreground-400 font-semibold mt-1">
                  <span>0</span>
                  <span>75 tys.</span>
                  <span>150 tys.</span>
                </div>
              </div>

              {/* Years slider */}
              <div className="mt-5">
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-xs font-bold text-foreground-500 uppercase tracking-wider">
                    Okres nowego kredytu
                  </span>
                  <span className="text-sm font-bold text-foreground-950">
                    {years} {yearsLabel}
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={20}
                  step={1}
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  className="w-full accent-primary-500"
                />
                <div className="flex justify-between text-[10px] text-foreground-400 font-semibold mt-1">
                  <span>1</span>
                  <span>10</span>
                  <span>20</span>
                </div>
              </div>

              {/* Rate slider */}
              <div className="mt-5">
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-xs font-bold text-foreground-500 uppercase tracking-wider">
                    Oprocentowanie konsolidacji
                  </span>
                  <span className="text-sm font-bold text-foreground-950">
                    {rate.toFixed(2).replace('.', ',')}%
                  </span>
                </div>
                <input
                  type="range"
                  min={5}
                  max={16}
                  step={0.1}
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  className="w-full accent-primary-500"
                />
                <div className="flex justify-between text-[10px] text-foreground-400 font-semibold mt-1">
                  <span>5%</span>
                  <span>10,5%</span>
                  <span>16%</span>
                </div>
                <p className="text-[11px] text-foreground-400 mt-2 leading-relaxed">
                  Orientacyjnie: konsolidacja gotówkowa 9–13%, zabezpieczona hipoteką 6,5–8%. Ekspert kredytowy sprawdzi, na co realnie się kwalifikujesz.
                </p>
              </div>
            </div>

            {/* Right — Results */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Efekt konsolidacji
              </h3>

              <div
                className={`rounded-xl p-6 text-center ${
                  isSaving ? 'bg-green-700 text-white' : 'bg-foreground-950 text-white'
                }`}
              >
                <div
                  className={`text-[11px] font-bold tracking-[0.15em] uppercase mb-2 ${
                    isSaving ? 'text-green-300' : 'text-primary-400'
                  }`}
                >
                  {isSaving
                    ? 'W kieszeni zostaje co miesiąc'
                    : diff < -10
                      ? 'Wynik porównania'
                      : 'Wynik porównania'}
                </div>
                <div className="text-2xl md:text-3xl font-extrabold tracking-tight">
                  {isSaving
                    ? `+${fmt(diff)}`
                    : diff < -10
                      ? 'Rata będzie wyższa'
                      : 'Rata bez zmian'}
                </div>
                <div
                  className={`text-xs mt-1 ${
                    isSaving ? 'text-green-200' : 'text-white/60'
                  }`}
                >
                  {isSaving
                    ? `rata niższa o ${Math.round((diff / oldRaty) * 100)}% — ${fmt(diff * 12)} rocznie`
                    : 'przy tym okresie i oprocentowaniu konsolidacja nie obniży raty'}
                </div>
              </div>

              {/* VS */}
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="border border-background-200 rounded-xl py-4 px-3 text-center">
                  <div className="text-[10px] font-bold tracking-wider uppercase text-foreground-500 mb-1">
                    Dziś płacisz
                  </div>
                  <div className="text-xl font-extrabold text-foreground-950 tracking-tight">
                    {fmt(oldRaty)}
                  </div>
                  <div className="text-[10px] text-foreground-400 font-semibold">
                    {debts.length} {debts.length === 1 ? 'rata' : debts.length < 5 ? 'raty' : 'rat'}
                  </div>
                </div>
                <div className="border border-green-300 bg-green-50 rounded-xl py-4 px-3 text-center">
                  <div className="text-[10px] font-bold tracking-wider uppercase text-foreground-500 mb-1">
                    Po konsolidacji
                  </div>
                  <div className="text-xl font-extrabold text-green-700 tracking-tight">
                    {fmt(newRata)}
                  </div>
                  <div className="text-[10px] text-foreground-400 font-semibold">
                    jedna rata
                  </div>
                </div>
              </div>

              {/* Sum rows */}
              <div className="mt-4">
                {[
                  { label: 'Łączne zadłużenie do spłaty', value: fmt(saldo) },
                  ...(cash > 0
                    ? [{ label: '+ dodatkowa gotówka', value: fmt(cash) }]
                    : []),
                  { label: 'Kwota nowego kredytu', value: fmt(loan) },
                  { label: 'Suma spłat nowego kredytu', value: fmt(totalNew) },
                  { label: 'Odsetki nowego kredytu', value: fmt(totalInt) },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex justify-between gap-3 py-2.5 border-b border-background-200 text-sm"
                  >
                    <span className="text-foreground-500">{row.label}</span>
                    <span className="font-bold text-foreground-950 whitespace-nowrap">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Hint */}
              <div
                className={`rounded-xl p-4 text-xs leading-relaxed mt-4 ${
                  isSaving
                    ? 'bg-primary-50 text-primary-800 border border-primary-200'
                    : 'bg-amber-50 text-amber-800 border border-amber-200'
                }`}
              >
                {isSaving ? (
                  <>
                    Pamiętaj o drugiej stronie medalu: niższa rata to zwykle{' '}
                    <b>dłuższa spłata i wyższy koszt całkowity</b>. Ekspert kredytowy
                    policzy na Twoich umowach, czy konsolidacja się broni — i
                    czy wersja hipoteczna (od ok. 6,5%) nie da raty jeszcze
                    niższej.
                  </>
                ) : (
                  <>
                    Spróbuj wydłużyć okres lub obniżyć oprocentowanie
                    (konsolidacja hipoteczna). Jeśli celem nie jest niższa rata,
                    ale <b>szybsze wyjście z długów</b>, ekspert kredytowy pokaże
                    scenariusz optymalny.
                  </>
                )}
              </div>

              <p className="text-[10px] text-foreground-400 mt-4 leading-relaxed">
                Obliczenia dla rat równych. Kalkulator porównuje wysokość rat
                (płynność miesięczną) — całkowity koszt konsolidacji zależy
                też od warunków spłacanych umów (odsetki, prowizje za
                wcześniejszą spłatę), których kalkulator nie zna. Pełne
                porównanie kosztowe przygotuje ekspert kredytowy na podstawie Twoich umów.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-foreground-950 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden mt-6">
            <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-primary-500/10 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">
                Odzyskaj kontrolę nad ratami — bezpłatna analiza konsolidacji
              </h2>
              <p className="text-sm text-white/60 max-w-xl mx-auto mb-8 leading-relaxed">
                Ekspert kredytowy przejrzy Twoje umowy, policzy realny koszt konsolidacji
                w kilku bankach i sprawdzi, czy lepsza będzie wersja gotówkowa
                czy hipoteczna. Ty decydujesz — z pełnym obrazem.
              </p>
              <a
                href="tel:698084044"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-white font-extrabold text-lg rounded-full hover:bg-primary-600 transition-all hover:-translate-y-0.5 shadow-lg shadow-primary-500/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl" />
                698 084 044
              </a>
              <div className="flex flex-wrap gap-5 justify-center mt-6">
                {[
                  'Bezpłatna analiza umów',
                  'Oferty wielu banków',
                  'Bez zobowiązań',
                ].map((t) => (
                  <span
                    key={t}
                    className="flex items-center gap-2 text-xs text-white/50"
                  >
                    <i className="ri-check-line text-green-400" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}